import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import UsuariosTab from "./components/UsuariosTab";
import RolesTab from "./components/RolesTab";
import ActividadTab from "./components/ActividadTab";
import NotificacionesTab from "./components/NotificacionesTab";
import AuditoriaTab from "./components/AuditoriaTab";
import UsuarioModal from "./components/UsuarioModal";
import RolModal from "./components/RolModal";
import {
  getNotificacionLogDescripcion,
  getNotificacionLogEstadoBadge,
} from "./components/notificacionLogUtils";
import {
  AjustesSection,
  UsuarioDto,
  UsuarioEmpresaDto,
  RolDto,
  UsuarioFormState,
  RolFormState,
  PermisoDto,
  ActividadUsuarioDto,
  AuditoriaUsuarioDto,
  NotificacionDto,
  SucursalCatalogo,
  UsuarioSucursalForm,
  EmpresaCatalogo,
  NotificacionFormState,
  NotificacionDestinoForm,
  SucursalNotificacionCatalogo,
  NotificacionLogDto,
  SesionActividadDto,
} from "../../types/ajustes";
import { ApiError, requestJson } from "../../helpers/api";
import { obtenerSesionContexto } from "../../helpers/sesionesScope";

const defaultUsuarioForm: UsuarioFormState = {
  nombreUsuario: "",
  numeroCi: "",
  contrasena: "",
  correoCorporativo: "",
  requiereDobleFactor: false,
  rolesIds: [],
  sucursales: [],
  empresasIds: [],
};

const defaultRolForm: RolFormState = {
  nombre: "",
  descripcion: "",
  permisos: [],
  activo: true,
};

const defaultNotificacionForm: NotificacionFormState = {
  empresaCodigo: "",
  empresaNombre: "",
  idsucursal: "",
  sucursalNombre: "",
  destinosBatch: [],
  correo: "",
  descripcion: "",
  activo: true,
};

const construirClaveDestino = (empresaCodigo: string, idsucursal: string) => {
  const empresa = (empresaCodigo ?? "").trim();
  const sucursal = (idsucursal ?? "").trim();
  if (!empresa) {
    return "";
  }
  return `${empresa}__${sucursal || "ALL"}`;
};

const construirEtiquetaDestino = (
  empresaCodigo: string,
  idsucursal: string,
  sucursalNombre: string,
) => {
  const descripcionSucursal =
    idsucursal.trim().length > 0
      ? `${idsucursal.trim()} · ${sucursalNombre?.trim() || "Sin nombre"}`
      : "Todas las sucursales";
  const empresa = empresaCodigo?.trim() || "Empresa sin código";
  return `${empresa} – ${descripcionSucursal}`;
};

const crearDestinoFormDesdeValores = (
  empresaCodigo: string,
  empresaNombre: string,
  idsucursal: string,
  sucursalNombre: string,
): NotificacionDestinoForm | null => {
  const clave = construirClaveDestino(empresaCodigo, idsucursal);
  if (!clave) {
    return null;
  }
  return {
    key: clave,
    empresaCodigo: empresaCodigo.trim(),
    empresaNombre: (empresaNombre ?? "").trim(),
    idsucursal: idsucursal.trim(),
    sucursalNombre: (sucursalNombre ?? "").trim(),
    label: construirEtiquetaDestino(empresaCodigo, idsucursal, sucursalNombre),
  };
};

const AjustesPage = () => {
  const [activeTab, setActiveTab] = useState<AjustesSection>("usuarios");

  const [usuarios, setUsuarios] = useState<UsuarioDto[]>([]);
  const [usuariosLoading, setUsuariosLoading] = useState(false);
  const [usuariosError, setUsuariosError] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(true);
  const [usuariosSearch, setUsuariosSearch] = useState("");

  const [roles, setRoles] = useState<RolDto[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [permisos, setPermisos] = useState<PermisoDto[]>([]);
  const [sucursalesDisponibles, setSucursalesDisponibles] = useState<SucursalCatalogo[]>([]);
  const [cargandoSucursales, setCargandoSucursales] = useState(false);
  const [errorSucursales, setErrorSucursales] = useState<string | null>(null);
  const [empresasDisponibles, setEmpresasDisponibles] = useState<EmpresaCatalogo[]>([]);
  const [cargandoEmpresas, setCargandoEmpresas] = useState(false);
  const [errorEmpresas, setErrorEmpresas] = useState<string | null>(null);

  const [actividad, setActividad] = useState<ActividadUsuarioDto[]>([]);
  const [actividadLoading, setActividadLoading] = useState(false);
  const [actividadError, setActividadError] = useState<string | null>(null);
  const [sesionesActivas, setSesionesActivas] = useState<SesionActividadDto[]>([]);
  const [sesionesRecientes, setSesionesRecientes] = useState<SesionActividadDto[]>([]);
  const [actividadUltimaCarga, setActividadUltimaCarga] = useState<number | null>(null);
  const [auditoriaEventos, setAuditoriaEventos] = useState<AuditoriaUsuarioDto[]>([]);
  const [auditoriaLoading, setAuditoriaLoading] = useState(false);
  const [auditoriaError, setAuditoriaError] = useState<string | null>(null);

  const [notificaciones, setNotificaciones] = useState<NotificacionDto[]>([]);
  const [notificacionesLoading, setNotificacionesLoading] = useState(false);
  const [notificacionesError, setNotificacionesError] = useState<string | null>(null);
  const [notificacionForm, setNotificacionForm] =
    useState<NotificacionFormState>(defaultNotificacionForm);
  const [notificacionEditing, setNotificacionEditing] = useState<NotificacionDto | null>(null);
  const [notificacionSaving, setNotificacionSaving] = useState(false);
  const [notificacionFormError, setNotificacionFormError] = useState<string | null>(null);
  const [notificacionFormSuccess, setNotificacionFormSuccess] = useState<string | null>(null);
  const [notificacionCatalogo, setNotificacionCatalogo] = useState<SucursalNotificacionCatalogo[]>(
    []
  );
  const [notificacionCatalogoLoading, setNotificacionCatalogoLoading] = useState(false);
  const [notificacionCatalogoError, setNotificacionCatalogoError] = useState<string | null>(null);
  const [notificacionLogs, setNotificacionLogs] = useState<NotificacionLogDto[]>([]);
  const [notificacionLogsLoading, setNotificacionLogsLoading] = useState(false);
  const [notificacionLogsError, setNotificacionLogsError] = useState<string | null>(null);
  const [notificacionLogsModalOpen, setNotificacionLogsModalOpen] = useState(false);
  const [notificacionLogsTarget, setNotificacionLogsTarget] = useState<NotificacionDto | null>(null);
  const [notificacionResumenLogs, setNotificacionResumenLogs] = useState<NotificacionLogDto[]>([]);
  const [notificacionResumenLoading, setNotificacionResumenLoading] = useState(false);
  const [notificacionResumenError, setNotificacionResumenError] = useState<string | null>(null);
  const [logPreviewHtml, setLogPreviewHtml] = useState<string | null>(null);
  const [logPreviewMeta, setLogPreviewMeta] = useState<{ correo: string; fecha: string } | null>(null);
  const [logPreviewOpen, setLogPreviewOpen] = useState(false);
  const notificacionesAutoLoaded = useRef(false);
  const notificacionCatalogoAutoLoaded = useRef(false);
  const notificacionResumenAutoLoaded = useRef(false);
  const auditoriaAutoLoaded = useRef(false);
  const sesionContexto = useMemo(() => obtenerSesionContexto(), []);
  const permisosSesion = useMemo(() => new Set(sesionContexto.permisos ?? []), [sesionContexto]);
  const tienePermiso = useCallback((codigo: string) => permisosSesion.has(codigo), [permisosSesion]);
  const tieneAlMenosUno = useCallback(
    (codigos: string[]) => codigos.some((codigo) => permisosSesion.has(codigo)),
    [permisosSesion]
  );

  const puedeVerUsuarios = tienePermiso("usuarios.ver");
  const puedeCrearUsuario = tienePermiso("usuarios.crear");
  const puedeEditarUsuario = tienePermiso("usuarios.editar");
  const puedeEliminarUsuario = tienePermiso("usuarios.eliminar");
  const puedeVerRoles = tienePermiso("roles.ver");
  const puedeCrearRol = tienePermiso("roles.crear");
  const puedeEditarRol = tienePermiso("roles.editar");
  const puedeVerAuditoria = tienePermiso("usuarios.ver_auditoria");
  const puedeVerActividad = tienePermiso("usuarios.ver_actividad");
  const puedeVerSesiones = tieneAlMenosUno(["usuarios.ver", "usuarios.ver-activos", "general.ver_perfil"]);
  const puedeConfigurarNotificaciones = tienePermiso("controlcaja.configurar_notificaciones");

  const [usuarioModalOpen, setUsuarioModalOpen] = useState(false);
  const [usuarioForm, setUsuarioForm] = useState<UsuarioFormState>({
    ...defaultUsuarioForm,
    sucursales: [],
    empresasIds: [],
  });
  const [usuarioEditing, setUsuarioEditing] = useState<UsuarioDto | null>(null);
  const [usuarioSaving, setUsuarioSaving] = useState(false);

  const [rolModalOpen, setRolModalOpen] = useState(false);
  const [rolForm, setRolForm] = useState<RolFormState>(defaultRolForm);
  const [rolEditing, setRolEditing] = useState<RolDto | null>(null);
  const [rolSaving, setRolSaving] = useState(false);

  const handleApiError = useCallback((error: unknown, fallback: string) => {
    if (error instanceof ApiError) {
      return error.message;
    }
    return fallback;
  }, []);

  const dismissNotificacionFeedback = useCallback(() => {
    setNotificacionFormError(null);
    setNotificacionFormSuccess(null);
  }, []);

  const handleNotificacionFormChange = useCallback(
    (nextForm: NotificacionFormState) => {
      setNotificacionForm(nextForm);
      dismissNotificacionFeedback();
    },
    [dismissNotificacionFeedback]
  );

  const resetNotificacionForm = useCallback(() => {
    setNotificacionForm({ ...defaultNotificacionForm });
    setNotificacionEditing(null);
  }, []);

  const normalizeUsuarioSucursales = (raw: unknown): UsuarioSucursalForm[] => {
    const resolveArray = (value: unknown): unknown[] => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch {
          return [];
        }
      }
      if (value && typeof value === "object") {
        const contenedor = value as { data?: unknown[]; items?: unknown[]; value?: unknown[] };
        if (Array.isArray(contenedor.data)) return contenedor.data;
        if (Array.isArray(contenedor.items)) return contenedor.items;
        if (Array.isArray(contenedor.value)) return contenedor.value;
      }
      return [];
    };

    const candidatos = resolveArray(raw);
    const resultado: UsuarioSucursalForm[] = [];
    const claves = new Set<string>();

    candidatos.forEach((entrada) => {
      if (!entrada || typeof entrada !== "object") return;
      const fuente =
        (entrada as any).fuente ??
        (entrada as any).source ??
        (entrada as any).servidor ??
        (entrada as any).base ??
        (entrada as any).basedatos ??
        null;
      const idValor =
        (entrada as any).idsucursal ??
        (entrada as any).idSucursal ??
        (entrada as any).id_sucursal ??
        (entrada as any).sucursalId ??
        (entrada as any).id ??
        null;
      const nombre =
        (entrada as any).sucursalNombre ??
        (entrada as any).sucursal_nombre ??
        (entrada as any).nombre ??
        (entrada as any).nombreSucursal ??
        (entrada as any).sucursal ??
        null;
      if (fuente == null || idValor == null) return;
      const idsucursal = Number(idValor);
      if (Number.isNaN(idsucursal)) return;
      const clave = `${String(fuente)}__${idsucursal}`;
      if (claves.has(clave)) return;
      claves.add(clave);
      resultado.push({
        fuente: String(fuente),
        idsucursal,
        sucursalNombre: typeof nombre === "string" && nombre.trim().length > 0 ? nombre : null,
      });
    });

    return resultado;
  };

  const normalizeUsuarioEmpresas = (raw: unknown): UsuarioEmpresaDto[] => {
    const resolveArray = (value: unknown): unknown[] => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch {
          return [];
        }
      }
      if (value && typeof value === "object") {
        const contenedor = value as { data?: unknown[]; items?: unknown[]; value?: unknown[] };
        if (Array.isArray(contenedor.data)) return contenedor.data;
        if (Array.isArray(contenedor.items)) return contenedor.items;
        if (Array.isArray(contenedor.value)) return contenedor.value;
      }
      return [];
    };

    const candidatos = resolveArray(raw);
    const resultado: UsuarioEmpresaDto[] = [];
    const ids = new Set<number>();
    candidatos.forEach((item, index) => {
      if (!item) return;
      if (typeof item === "number") {
        if (!ids.has(item)) {
          ids.add(item);
          resultado.push({
            id: item,
            codigo: null,
            nombre: `Empresa ${item}`,
          });
        }
        return;
      }
      if (typeof item !== "object") {
        return;
      }
      const registro = item as any;
      const empresa =
        typeof registro.empresa === "object" && registro.empresa !== null ? registro.empresa : null;
      const id =
        Number(registro.id ?? registro.empresaId ?? registro.empresa_id ?? empresa?.id ?? NaN) ||
        NaN;
      if (!Number.isFinite(id) || ids.has(id)) {
        return;
      }
      ids.add(id);
      resultado.push({
        id,
        codigo: registro.codigo ?? empresa?.codigo ?? null,
        nombre:
          registro.nombre ??
          empresa?.nombre ??
          registro.descripcion ??
          registro.razonSocial ??
          registro.razon_social ??
          null,
      });
    });
    return resultado;
  };

  const fetchUsuarios = useCallback(async (includeDeleted = false) => {
    setUsuariosLoading(true);
    setUsuariosError(null);
    try {
      const query = includeDeleted ? "?includeDeleted=true" : "";
      const raw = await requestJson<any>("/api/usuarios" + query);
      const data: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.usuarios)
        ? raw.usuarios
        : [];
      const normalizados: UsuarioDto[] = data.map((item, index) => {
        const nombreUsuario =
          item.nombreUsuario || item.nombre_usuario || item.nombre || `Usuario ${index + 1}`;
        const correoCorporativo = item.correoCorporativo ?? item.correo_corporativo ?? null;
        const requiereDobleFactor =
          item.requiereDobleFactor ?? item.requiere_doble_factor ?? false;
        const numeroCi = item.numeroCi ?? item.numero_ci ?? null;

        const rolesNormalizados: RolDto[] = Array.isArray(item.roles)
          ? item.roles.map((rol: any, idx: number) => ({
              id: rol.id ?? idx,
              nombre: rol.nombre ?? (typeof rol === "string" ? rol : `Rol ${idx + 1}`),
              descripcion: rol.descripcion ?? null,
              activo: rol.activo ?? true,
              permisos: Array.isArray(rol.permisos) ? rol.permisos : [],
              usuariosAsignados: rol.usuariosAsignados ?? rol.usuarios_asignados ?? rol.usuarios ?? undefined,
            }))
          : typeof item.roles === "string"
          ? item.roles
              .replace(/[{}]/g, "")
              .split(",")
              .map((nombre: string, idx: number) => ({
                id: idx,
                nombre: nombre.trim(),
                descripcion: null,
                activo: true,
                permisos: [],
                usuariosAsignados: undefined,
              }))
          : [];
        const sucursalesRaw =
          item.sucursales ??
          item.usuarioSucursales ??
          item.usuario_sucursales ??
          item.sucursalesAsignadas ??
          item.sucursales_asignadas ??
          item.detalleSucursales ??
          [];
        const sucursalesNormalizadas = normalizeUsuarioSucursales(sucursalesRaw);
        const empresasRaw =
          item.empresas ??
          item.empresasAsignadas ??
          item.empresas_asignadas ??
          item.empresasRelacionadas ??
          [];
        const empresasNormalizadas = normalizeUsuarioEmpresas(empresasRaw);

        return {
          id: item.id,
          nombreUsuario,
          numeroCi,
          correoCorporativo,
          requiereDobleFactor,
          roles: rolesNormalizados,
          permisos: Array.isArray(item.permisos) ? item.permisos : [],
          eliminado: Boolean(item.eliminado ?? item.eliminado_en),
          eliminadoEn: item.eliminadoEn ?? item.eliminado_en ?? null,
          sucursales: sucursalesNormalizadas,
          empresas: empresasNormalizadas,
        };
      });
      setUsuarios(normalizados);
    } catch (error) {
      setUsuariosError(handleApiError(error, "No se pudieron obtener los usuarios."));
    } finally {
      setUsuariosLoading(false);
    }
  }, [handleApiError]);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      const [rolesData, permisosData] = await Promise.all([
        requestJson<RolDto[]>("/api/usuarios/roles"),
        requestJson<PermisoDto[]>("/api/usuarios/permisos"),
      ]);
      setRoles(rolesData);
      setPermisos(permisosData);
    } catch (error) {
      setRolesError(handleApiError(error, "No se pudieron obtener los roles."));
    } finally {
      setRolesLoading(false);
    }
  }, [handleApiError]);

  const alcanceSesion = sesionContexto.alcance;
  const fetchActividad = useCallback(async () => {
    if (!puedeVerActividad) {
      setActividad([]);
      setSesionesActivas([]);
      setSesionesRecientes([]);
      setActividadUltimaCarga(null);
      setActividadError("No contás con permisos para ver la actividad.");
      setActividadLoading(false);
      return;
    }
    setActividadLoading(true);
    setActividadError(null);
    try {
      const paramsActivas = new URLSearchParams({
        limit: "12",
        alcance: alcanceSesion,
      });
      const paramsRecientes = new URLSearchParams({
        limit: "24",
        alcance: alcanceSesion,
      });
      const actividadPromise = requestJson<ActividadUsuarioDto[]>("/api/usuarios/actividad");
      const activasPromise = puedeVerSesiones
        ? requestJson<SesionActividadDto[]>(`/api/usuarios/sesiones-activas?${paramsActivas.toString()}`)
        : Promise.resolve([]);
      const recientesPromise = puedeVerSesiones
        ? requestJson<SesionActividadDto[]>(`/api/usuarios/sesiones-recientes?${paramsRecientes.toString()}`)
        : Promise.resolve([]);
      const [actividadData, activasData, recientesData] = await Promise.all([
        actividadPromise,
        activasPromise,
        recientesPromise,
      ]);
      setActividad(Array.isArray(actividadData) ? actividadData : []);
      setSesionesActivas(Array.isArray(activasData) ? activasData : []);
      setSesionesRecientes(Array.isArray(recientesData) ? recientesData : []);
      setActividadUltimaCarga(Date.now());
    } catch (error) {
      setActividadError(handleApiError(error, "No se pudo cargar la actividad."));
    } finally {
      setActividadLoading(false);
    }
  }, [alcanceSesion, handleApiError, puedeVerActividad, puedeVerSesiones]);

  const fetchAuditoria = useCallback(async () => {
    setAuditoriaLoading(true);
    setAuditoriaError(null);
    try {
      const data = await requestJson<AuditoriaUsuarioDto[]>("/api/usuarios/auditoria");
      setAuditoriaEventos(Array.isArray(data) ? data : []);
    } catch (error) {
      setAuditoriaError(handleApiError(error, "No se pudo cargar la auditoría."));
    } finally {
      setAuditoriaLoading(false);
    }
  }, [handleApiError]);

  const fetchNotificaciones = useCallback(async () => {
    setNotificacionesLoading(true);
    setNotificacionesError(null);
    try {
      const data = await requestJson<NotificacionDto[]>("/api/rendiciones/notificaciones");
      if (Array.isArray(data)) {
        setNotificaciones(data);
      }
    } catch (error) {
      setNotificacionesError(handleApiError(error, "No se pudieron obtener las notificaciones."));
    } finally {
      setNotificacionesLoading(false);
    }
  }, [handleApiError]);

  const fetchNotificacionesSucursales = useCallback(async () => {
    setNotificacionCatalogoLoading(true);
    setNotificacionCatalogoError(null);
    try {
      const data = await requestJson<SucursalNotificacionCatalogo[]>(
        "/api/tesoreria/mis-sucursales"
      );
      setNotificacionCatalogo(data);
    } catch (error) {
      setNotificacionCatalogoError(
        handleApiError(error, "No se pudieron obtener las sucursales.")
      );
    } finally {
      setNotificacionCatalogoLoading(false);
    }
  }, [handleApiError]);

  const startEditNotificacion = useCallback(
    (registro: NotificacionDto) => {
      const empresaCodigo = registro.empresaCodigo ?? "";
      const empresaNombre = registro.empresaNombre ?? "";
      const idsucursalTexto =
        registro.idsucursal !== undefined && registro.idsucursal !== null
          ? String(registro.idsucursal)
          : "";
      const sucursalNombre = registro.sucursalNombre ?? "";
      const destinoExistente =
        crearDestinoFormDesdeValores(empresaCodigo, empresaNombre, idsucursalTexto, sucursalNombre);
      handleNotificacionFormChange({
        empresaCodigo,
        empresaNombre,
        idsucursal: idsucursalTexto,
        sucursalNombre,
        destinosBatch: destinoExistente ? [destinoExistente] : [],
        correo: registro.correo,
        descripcion: registro.descripcion ?? "",
        activo: registro.activo,
      });
      setNotificacionEditing(registro);
    },
    [handleNotificacionFormChange]
  );

  const saveNotificacion = useCallback(async () => {
    const correoDestino = notificacionForm.correo.trim();
    if (!correoDestino) {
      setNotificacionFormError("Ingresá un correo de destino.");
      setNotificacionFormSuccess(null);
      return;
    }
    const destinosSeleccionados =
      notificacionForm.destinosBatch.length > 0
        ? notificacionForm.destinosBatch
        : (() => {
            const destinoUnico = crearDestinoFormDesdeValores(
              notificacionForm.empresaCodigo,
              notificacionForm.empresaNombre,
              notificacionForm.idsucursal,
              notificacionForm.sucursalNombre,
            );
            return destinoUnico ? [destinoUnico] : [];
          })();
    if (destinosSeleccionados.length === 0) {
      setNotificacionFormError("Seleccioná al menos una empresa y sucursal.");
      setNotificacionFormSuccess(null);
      return;
    }
    setNotificacionSaving(true);
    setNotificacionFormError(null);
    setNotificacionFormSuccess(null);
    try {
      const descripcionNormalizada = notificacionForm.descripcion.trim();
      const payloadDesdeDestino = (destino: NotificacionDestinoForm) => ({
        empresaCodigo: destino.empresaCodigo.trim().toUpperCase(),
        empresaNombre: destino.empresaNombre.trim() || undefined,
        idsucursal: destino.idsucursal.trim() ? Number(destino.idsucursal.trim()) : undefined,
        sucursalNombre: destino.sucursalNombre.trim() || undefined,
      });
      if (notificacionEditing) {
        const destino = destinosSeleccionados[0];
        await requestJson(`/api/rendiciones/notificaciones/${notificacionEditing.id}`, {
          method: "PUT",
          body: JSON.stringify({
            ...payloadDesdeDestino(destino),
            correo: correoDestino,
            descripcion: descripcionNormalizada || undefined,
            activo: notificacionForm.activo,
          }),
        });
        setNotificacionFormSuccess("Destinatario actualizado correctamente.");
      } else {
        for (const destino of destinosSeleccionados) {
          await requestJson("/api/rendiciones/notificaciones", {
            method: "POST",
            body: JSON.stringify({
              ...payloadDesdeDestino(destino),
              correo: correoDestino,
              descripcion: descripcionNormalizada || undefined,
              activo: notificacionForm.activo,
            }),
          });
        }
        setNotificacionFormSuccess(
          destinosSeleccionados.length > 1
            ? `${destinosSeleccionados.length} destinatarios agregados correctamente.`
            : "Destinatario agregado correctamente."
        );
      }
      resetNotificacionForm();
      fetchNotificaciones();
    } catch (error) {
      setNotificacionFormError(handleApiError(error, "No se pudo guardar el destinatario."));
      setNotificacionFormSuccess(null);
    } finally {
      setNotificacionSaving(false);
    }
  }, [
    fetchNotificaciones,
    notificacionEditing,
    notificacionForm.activo,
    notificacionForm.correo,
    notificacionForm.descripcion,
    notificacionForm.destinosBatch,
    notificacionForm.empresaCodigo,
    notificacionForm.empresaNombre,
    notificacionForm.idsucursal,
    notificacionForm.sucursalNombre,
    resetNotificacionForm,
    handleApiError,
  ]);

  const deleteNotificacion = useCallback(
    async (registro: NotificacionDto) => {
      const confirmar = window.confirm(
        `¿Deseas eliminar la notificación de ${registro.correo}?`
      );
      if (!confirmar) return;
      setNotificacionFormError(null);
      setNotificacionFormSuccess(null);
      try {
        await requestJson(`/api/rendiciones/notificaciones/${registro.id}`, { method: "DELETE" });
        if (notificacionEditing && notificacionEditing.id === registro.id) {
          resetNotificacionForm();
        }
        setNotificacionFormSuccess("Destinatario eliminado correctamente.");
        fetchNotificaciones();
      } catch (error) {
        setNotificacionFormError(
          handleApiError(error, "No se pudo eliminar el destinatario seleccionado.")
        );
      }
    },
    [fetchNotificaciones, handleApiError, notificacionEditing, resetNotificacionForm]
  );

  const cancelNotificacionEdition = useCallback(() => {
    resetNotificacionForm();
    dismissNotificacionFeedback();
  }, [dismissNotificacionFeedback, resetNotificacionForm]);

  const fetchNotificacionLogs = useCallback(
    async (notificacionId: number, limit = 50) => {
      setNotificacionLogsLoading(true);
      setNotificacionLogsError(null);
      try {
        const data = await requestJson<NotificacionLogDto[]>(
          `/api/rendiciones/notificaciones/${notificacionId}/logs?limit=${limit}`
        );
        setNotificacionLogs(Array.isArray(data) ? data : []);
      } catch (error) {
        setNotificacionLogs([]);
        setNotificacionLogsError(
          handleApiError(error, "No se pudo obtener el historial de envíos.")
        );
      } finally {
        setNotificacionLogsLoading(false);
      }
    },
    [handleApiError]
  );

  const fetchNotificacionResumenLogs = useCallback(async () => {
    setNotificacionResumenLoading(true);
    setNotificacionResumenError(null);
    try {
      const data = await requestJson<NotificacionLogDto[]>(
        "/api/rendiciones/notificaciones/logs?limit=50"
      );
      setNotificacionResumenLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      setNotificacionResumenLogs([]);
      setNotificacionResumenError(
        handleApiError(error, "No se pudo obtener el historial general de notificaciones.")
      );
    } finally {
      setNotificacionResumenLoading(false);
    }
  }, [handleApiError]);

  const openNotificacionLogs = useCallback(
    (registro: NotificacionDto) => {
      setNotificacionLogsTarget(registro);
      setNotificacionLogs([]);
      setNotificacionLogsError(null);
      setNotificacionLogsModalOpen(true);
      fetchNotificacionLogs(registro.id);
    },
    [fetchNotificacionLogs]
  );

  const closeNotificacionLogs = useCallback(() => {
    setNotificacionLogsModalOpen(false);
    setNotificacionLogsTarget(null);
  }, []);

  const openLogPreview = useCallback((log: NotificacionLogDto) => {
    if (!log.cuerpoHtml) {
      return;
    }
    setLogPreviewHtml(log.cuerpoHtml);
    setLogPreviewMeta({
      correo: log.correo,
      fecha: new Date(log.intentoEn).toLocaleString(),
    });
    setLogPreviewOpen(true);
  }, []);

  const closeLogPreview = useCallback(() => {
    setLogPreviewOpen(false);
    setLogPreviewHtml(null);
    setLogPreviewMeta(null);
  }, []);

  const fetchSucursales = useCallback(async () => {
    setCargandoSucursales(true);
    setErrorSucursales(null);
    try {
      const data = await requestJson<SucursalCatalogo[]>("/api/usuarios/sucursales");
      setSucursalesDisponibles(data);
    } catch (error) {
      setErrorSucursales(handleApiError(error, "No se pudieron cargar las sucursales."));
    } finally {
      setCargandoSucursales(false);
    }
  }, [handleApiError]);

  const fetchEmpresas = useCallback(async () => {
    setCargandoEmpresas(true);
    setErrorEmpresas(null);
    try {
      const data = await requestJson<EmpresaCatalogo[]>("/api/usuarios/empresas");
      const lista = Array.isArray(data) ? data : [];
      setEmpresasDisponibles(
        lista.map((empresa, index) => ({
          id: empresa.id ?? index,
          codigo: empresa.codigo ?? null,
          nombre: empresa.nombre ?? empresa.codigo ?? `Empresa ${index + 1}`,
        }))
      );
    } catch (error) {
      setErrorEmpresas(handleApiError(error, "No se pudieron cargar las empresas."));
    } finally {
      setCargandoEmpresas(false);
    }
  }, [handleApiError]);

  const rolesConAsignaciones = useMemo(() => {
    const conteo = new Map<number, number>();
    usuarios.forEach((usuario) => {
      usuario.roles.forEach((rol) => {
        conteo.set(rol.id, (conteo.get(rol.id) ?? 0) + 1);
      });
    });
    return roles.map((rol) => ({
      ...rol,
      usuariosAsignados: conteo.get(rol.id) ?? rol.usuariosAsignados ?? 0,
    }));
  }, [roles, usuarios]);

  useEffect(() => {
    if (!puedeVerUsuarios) {
      setUsuarios([]);
      setUsuariosLoading(false);
      setUsuariosError(null);
      return;
    }
    fetchUsuarios(showDeleted);
  }, [fetchUsuarios, showDeleted, puedeVerUsuarios]);

  useEffect(() => {
    if (!puedeVerRoles) {
      setRoles([]);
      setPermisos([]);
      setRolesLoading(false);
      setRolesError(null);
      return;
    }
    fetchRoles();
  }, [fetchRoles, puedeVerRoles]);

  useEffect(() => {
    if (!puedeVerUsuarios) {
      setSucursalesDisponibles([]);
      setCargandoSucursales(false);
      setErrorSucursales(null);
      return;
    }
    fetchSucursales();
  }, [fetchSucursales, puedeVerUsuarios]);

  useEffect(() => {
    if (!puedeVerUsuarios) {
      setEmpresasDisponibles([]);
      setCargandoEmpresas(false);
      setErrorEmpresas(null);
      return;
    }
    fetchEmpresas();
  }, [fetchEmpresas, puedeVerUsuarios]);

  useEffect(() => {
    if (activeTab !== "actividades" || !puedeVerActividad) {
      return;
    }
    fetchActividad();
    const interval = setInterval(() => {
      fetchActividad();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, fetchActividad, puedeVerActividad]);

  useEffect(() => {
    if (activeTab === "notificaciones" && puedeConfigurarNotificaciones) {
      if (!notificacionesAutoLoaded.current) {
        notificacionesAutoLoaded.current = true;
        fetchNotificaciones();
      }
      if (!notificacionCatalogoAutoLoaded.current) {
        notificacionCatalogoAutoLoaded.current = true;
        fetchNotificacionesSucursales();
      }
      if (!notificacionResumenAutoLoaded.current) {
        notificacionResumenAutoLoaded.current = true;
        fetchNotificacionResumenLogs();
      }
    } else {
      notificacionesAutoLoaded.current = false;
      notificacionCatalogoAutoLoaded.current = false;
      notificacionResumenAutoLoaded.current = false;
      if (!puedeConfigurarNotificaciones) {
        setNotificaciones([]);
        setNotificacionesError(null);
        setNotificacionCatalogo([]);
        setNotificacionCatalogoError(null);
        setNotificacionResumenLogs([]);
        setNotificacionResumenError(null);
      }
    }
  }, [
    activeTab,
    fetchNotificacionResumenLogs,
    fetchNotificaciones,
    fetchNotificacionesSucursales,
    puedeConfigurarNotificaciones,
  ]);

  useEffect(() => {
    if (activeTab === "auditoria" && puedeVerAuditoria) {
      if (!auditoriaAutoLoaded.current) {
        auditoriaAutoLoaded.current = true;
        fetchAuditoria();
      }
    } else {
      auditoriaAutoLoaded.current = false;
      if (!puedeVerAuditoria) {
        setAuditoriaEventos([]);
        setAuditoriaError(null);
      }
    }
  }, [activeTab, fetchAuditoria, puedeVerAuditoria]);

  const openCreateUsuario = () => {
    setUsuarioEditing(null);
    setUsuarioForm({
      ...defaultUsuarioForm,
      sucursales: [],
      empresasIds: [],
    });
    setUsuarioModalOpen(true);
  };

  const openEditUsuario = (usuario: UsuarioDto) => {
    setUsuarioEditing(usuario);
    setUsuarioForm({
      nombreUsuario: usuario.nombreUsuario,
      numeroCi: usuario.numeroCi ?? "",
      contrasena: "",
      correoCorporativo: usuario.correoCorporativo ?? "",
      requiereDobleFactor: usuario.requiereDobleFactor,
      rolesIds: usuario.roles.map((rol) => rol.id),
      sucursales: usuario.sucursales ?? [],
      empresasIds: (usuario.empresas ?? []).map((empresa) => empresa.id),
    });
    setUsuarioModalOpen(true);
  };

  const saveUsuario = async () => {
    if (usuarioEditing && !puedeEditarUsuario) {
      alert("No contás con permisos para editar usuarios.");
      return;
    }
    if (!usuarioEditing && !puedeCrearUsuario) {
      alert("No contás con permisos para crear usuarios.");
      return;
    }
    if (!usuarioForm.numeroCi.trim()) {
      alert("Ingresá el número de cédula del usuario.");
      return;
    }
    setUsuarioSaving(true);
    try {
      const payload = {
        ...usuarioForm,
        numeroCi: usuarioForm.numeroCi.trim(),
      };
      if (usuarioEditing) {
        await requestJson(`/api/usuarios/${usuarioEditing.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await requestJson("/api/usuarios", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setUsuarioModalOpen(false);
      if (puedeVerUsuarios) {
        fetchUsuarios(showDeleted);
      } else {
        setUsuarios([]);
      }
    } catch (error) {
      const message = handleApiError(error, "No se pudo guardar el usuario.");
      alert(message);
    } finally {
      setUsuarioSaving(false);
    }
  };

  const deleteUsuario = async (usuario: UsuarioDto) => {
    if (!puedeEliminarUsuario) {
      alert("No contás con permisos para inactivar usuarios.");
      return;
    }
    try {
      await requestJson(`/api/usuarios/${usuario.id}`, { method: "DELETE" });
      if (puedeVerUsuarios) {
        fetchUsuarios(showDeleted);
      }
    } catch (error) {
      alert(handleApiError(error, "No se pudo eliminar el usuario."));
    }
  };

  const reactivarUsuario = async (usuario: UsuarioDto) => {
    if (!puedeEditarUsuario) {
      alert("No contás con permisos para activar usuarios.");
      return;
    }
    try {
      await requestJson(`/api/usuarios/${usuario.id}/reactivar`, { method: "POST" });
      if (puedeVerUsuarios) {
        fetchUsuarios(showDeleted);
      }
    } catch (error) {
      alert(handleApiError(error, "No se pudo reactivar el usuario."));
    }
  };

  const openCreateRol = () => {
    setRolEditing(null);
    setRolForm(defaultRolForm);
    setRolModalOpen(true);
  };

  const openEditRol = (rol: RolDto) => {
    setRolEditing(rol);
    setRolForm({
      nombre: rol.nombre,
      descripcion: rol.descripcion ?? "",
      permisos: rol.permisos,
      activo: rol.activo,
    });
    setRolModalOpen(true);
  };

  const saveRol = async () => {
    if (rolEditing && !puedeEditarRol) {
      alert("No contás con permisos para editar roles.");
      return;
    }
    if (!rolEditing && !puedeCrearRol) {
      alert("No contás con permisos para crear roles.");
      return;
    }
    setRolSaving(true);
    try {
      if (rolEditing) {
        await requestJson(`/api/usuarios/roles/${rolEditing.id}`, {
          method: "PUT",
          body: JSON.stringify(rolForm),
        });
      } else {
        await requestJson("/api/usuarios/roles", {
          method: "POST",
          body: JSON.stringify(rolForm),
        });
      }
      setRolModalOpen(false);
      if (puedeVerRoles) {
        fetchRoles();
      } else {
        setRoles([]);
      }
    } catch (error) {
      alert(handleApiError(error, "No se pudo guardar el rol."));
    } finally {
      setRolSaving(false);
    }
  };

  const tabs: { id: AjustesSection; label: string; enabled: boolean }[] = useMemo(
    () => [
      { id: "usuarios", label: "Usuarios", enabled: puedeVerUsuarios },
      { id: "roles", label: "Roles", enabled: puedeVerRoles },
      { id: "auditoria", label: "Auditoría", enabled: puedeVerAuditoria },
      { id: "actividades", label: "Actividades", enabled: puedeVerActividad },
      { id: "notificaciones", label: "Notificaciones", enabled: puedeConfigurarNotificaciones },
    ],
    [puedeConfigurarNotificaciones, puedeVerActividad, puedeVerAuditoria, puedeVerRoles, puedeVerUsuarios]
  );
  const sinAccesoSecciones = tabs.every((tab) => !tab.enabled);
  useEffect(() => {
    if (tabs.some((tab) => tab.id === activeTab && tab.enabled)) {
      return;
    }
    const primeraHabilitada = tabs.find((tab) => tab.enabled);
    if (primeraHabilitada) {
      setActiveTab(primeraHabilitada.id);
    }
  }, [activeTab, tabs]);

  return (
    <div className="page-content ajustes-page">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb title="Ajustes" pageTitle="Configuración" />
        <Card>
          <CardBody>
            <Nav pills className="arrow-navtabs nav-success">
              {tabs.map((tab) => (
                <NavItem key={tab.id}>
                  <NavLink
                    href="#"
                    className={classNames({
                      active: activeTab === tab.id,
                      disabled: !tab.enabled,
                    })}
                    title={!tab.enabled ? "No contás con permisos para acceder a esta sección." : undefined}
                    onClick={(event) => {
                      event.preventDefault();
                      if (!tab.enabled) {
                        return;
                      }
                      setActiveTab(tab.id);
                    }}
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            {sinAccesoSecciones ? (
              <Alert color="warning" className="mt-4 mb-0">
                No contás con permisos para acceder a las secciones de Ajustes.
              </Alert>
            ) : (
              <TabContent activeTab={activeTab} className="pt-4">
                <TabPane tabId="usuarios">
                  {puedeVerUsuarios ? (
                    <UsuariosTab
                      usuarios={usuarios}
                      loading={usuariosLoading}
                      error={usuariosError}
                      onReload={() => fetchUsuarios(showDeleted)}
                      onCreate={openCreateUsuario}
                      onEdit={openEditUsuario}
                      onDelete={deleteUsuario}
                      onReactivate={reactivarUsuario}
                      showDeleted={showDeleted}
                      onToggleShowDeleted={setShowDeleted}
                      search={usuariosSearch}
                      onSearchChange={setUsuariosSearch}
                      canCreate={puedeCrearUsuario}
                      canEdit={puedeEditarUsuario}
                      canDelete={puedeEliminarUsuario}
                      canReactivate={puedeEditarUsuario}
                    />
                  ) : (
                    <Alert color="warning" className="mb-0">
                      No contás con permisos para ver el listado de usuarios.
                    </Alert>
                  )}
                </TabPane>
                <TabPane tabId="roles">
                  {puedeVerRoles ? (
                    <RolesTab
                      roles={rolesConAsignaciones}
                      loading={rolesLoading}
                      error={rolesError}
                      onReload={fetchRoles}
                      onCreate={openCreateRol}
                      onEdit={openEditRol}
                      canCreate={puedeCrearRol}
                      canEdit={puedeEditarRol}
                    />
                  ) : (
                    <Alert color="warning" className="mb-0">
                      No contás con permisos para ver los roles configurados.
                    </Alert>
                  )}
                </TabPane>
                <TabPane tabId="auditoria">
                  {puedeVerAuditoria ? (
                    <AuditoriaTab
                      eventos={auditoriaEventos}
                      loading={auditoriaLoading}
                      error={auditoriaError}
                      onReload={fetchAuditoria}
                    />
                  ) : (
                    <Alert color="warning" className="mb-0">
                      No contás con permisos para ver la auditoría de usuarios.
                    </Alert>
                  )}
                </TabPane>
                <TabPane tabId="actividades">
                  {puedeVerActividad ? (
                    <ActividadTab
                      actividades={actividad}
                      sesionesActivas={sesionesActivas}
                      sesionesRecientes={sesionesRecientes}
                      ultimaActualizacion={actividadUltimaCarga}
                      loading={actividadLoading}
                      error={actividadError}
                      onReload={fetchActividad}
                    />
                  ) : (
                    <Alert color="warning" className="mb-0">
                      No contás con permisos para revisar la actividad reciente.
                    </Alert>
                  )}
                </TabPane>
                <TabPane tabId="notificaciones">
                  {puedeConfigurarNotificaciones ? (
                    <>
                      <NotificacionesTab
                        registros={notificaciones}
                        form={notificacionForm}
                        enEdicion={Boolean(notificacionEditing)}
                        cargando={notificacionesLoading}
                        guardando={notificacionSaving}
                        error={notificacionesError}
                        onFormChange={handleNotificacionFormChange}
                        onSubmit={saveNotificacion}
                        onCancelar={cancelNotificacionEdition}
                        onReload={() => {
                          fetchNotificaciones();
                          fetchNotificacionesSucursales();
                          fetchNotificacionResumenLogs();
                        }}
                        onEditar={startEditNotificacion}
                        onEliminar={deleteNotificacion}
                        onVerHistorial={openNotificacionLogs}
                        catalogoSucursales={notificacionCatalogo}
                        catalogoCargando={notificacionCatalogoLoading}
                        catalogoError={notificacionCatalogoError}
                        formError={notificacionFormError}
                        formSuccess={notificacionFormSuccess}
                        onDismissFeedback={dismissNotificacionFeedback}
                        logs={notificacionResumenLogs}
                        logsLoading={notificacionResumenLoading}
                        logsError={notificacionResumenError}
                        onLogsReload={fetchNotificacionResumenLogs}
                        onVerEnvio={openLogPreview}
                      />
                    </>
                  ) : (
                    <Alert color="warning" className="mb-0">
                      No contás con permisos para gestionar las notificaciones de rendición.
                    </Alert>
                  )}
                </TabPane>
              </TabContent>
            )}
          </CardBody>
        </Card>
      </Container>

      <UsuarioModal
        isOpen={usuarioModalOpen}
        toggle={() => setUsuarioModalOpen(false)}
        formState={usuarioForm}
        onChange={(field, value) =>
          setUsuarioForm((prev) => ({
            ...prev,
            [field]: value,
          }))
        }
        onSubmit={saveUsuario}
        saving={usuarioSaving}
        roles={roles}
        sucursalesDisponibles={sucursalesDisponibles}
        cargandoSucursales={cargandoSucursales}
        errorSucursales={errorSucursales}
        onRecargarSucursales={fetchSucursales}
        empresasDisponibles={empresasDisponibles}
        cargandoEmpresas={cargandoEmpresas}
        errorEmpresas={errorEmpresas}
        onRecargarEmpresas={fetchEmpresas}
      />

      <RolModal
        isOpen={rolModalOpen}
        toggle={() => setRolModalOpen(false)}
        formState={rolForm}
        onChange={(field, value) =>
          setRolForm((prev) => ({
            ...prev,
            [field]: value,
          }))
        }
        onSubmit={saveRol}
        saving={rolSaving}
        permisos={permisos}
      />

      <Modal isOpen={notificacionLogsModalOpen} toggle={closeNotificacionLogs} size="lg">
        <ModalHeader toggle={closeNotificacionLogs}>
          Historial de envíos
        </ModalHeader>
        <ModalBody>
          {notificacionLogsTarget && (
            <div className="mb-3 text-muted small">
              <div>
                <strong>Correo:</strong> {notificacionLogsTarget.correo}
              </div>
              <div>
                <strong>Destino:</strong>{" "}
                {`${notificacionLogsTarget.empresaCodigo ?? "N/D"} · ${
                  notificacionLogsTarget.idsucursal ?? "Todas"
                }`}
              </div>
            </div>
          )}
          {notificacionLogsLoading ? (
            <div className="text-center text-muted py-4">Cargando historial...</div>
          ) : notificacionLogsError ? (
            <div className="alert alert-danger" role="alert">
              {notificacionLogsError}
            </div>
          ) : notificacionLogs.length === 0 ? (
            <div className="text-center text-muted py-4">
              No hay intentos de envío registrados.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {notificacionLogs.map((log) => {
                    const badge = getNotificacionLogEstadoBadge(log.estado);
                    return (
                      <tr key={log.id}>
                        <td>{new Date(log.intentoEn).toLocaleString()}</td>
                        <td>
                          <span className={badge.className}>{badge.label}</span>
                        </td>
                        <td className="text-muted">
                          {getNotificacionLogDescripcion(log)}
                          {log.cuerpoHtml && (
                            <div className="mt-2">
                              <Button color="link" size="sm" onClick={() => openLogPreview(log)}>
                                Ver envío
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={logPreviewOpen} toggle={closeLogPreview} size="lg">
        <ModalHeader toggle={closeLogPreview}>Vista previa del envío</ModalHeader>
        <ModalBody>
          {logPreviewMeta && (
            <div className="mb-2 text-muted small">
              <div>
                <strong>Correo:</strong> {logPreviewMeta.correo}
              </div>
              <div>
                <strong>Fecha de intento:</strong> {logPreviewMeta.fecha}
              </div>
            </div>
          )}
          {logPreviewHtml ? (
            <div className="ratio ratio-4x3">
              <iframe
                title="Vista previa de notificación"
                srcDoc={logPreviewHtml}
                className="w-100 h-100 border"
              />
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              No hay contenido disponible.
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default AjustesPage;
